import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import wdLogo from '../../assets/wd-logo.svg';

/* ─────────────────────────────────────────────
   NAV-SPECIFIC STYLES
   (These override / extend global.css safely)
───────────────────────────────────────────── */
const navStyles = `
  /* ── Base nav ── */
  .landing-nav {
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 60px;
    border-bottom: 1px solid var(--border-soft);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    background: rgba(10, 9, 0, 0.88);
    gap: 16px;
  }

  /* ── Logo ── */
  .landing-logo {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    line-height: 0;
    text-decoration: none;
  }
  .landing-logo-img {
    height: 36px;
    width: auto;
    display: block;
    object-fit: contain;
  }

  /* ── Desktop nav links ── */
  .nav-links {
    display: flex;
    align-items: center;
    gap: 32px;
    list-style: none;
    margin: 0; padding: 0;
    flex: 1;
    justify-content: center;
  }
  .nav-links li { display: flex; }
  .nav-links button {
    background: none;
    border: none;
    padding: 0;
    color: var(--muted-light);
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    transition: color 0.2s;
    white-space: nowrap;
  }
  .nav-links button:hover { color: var(--white); }

  /* ── Desktop CTA ── */
  .nav-cta {
    background: var(--accent);
    color: var(--black);
    padding: 9px 22px;
    border-radius: 2px;
    font-size: 11px;
    letter-spacing: 2px;
    text-transform: uppercase;
    border: none;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    transition: background 0.25s;
    white-space: nowrap;
    flex-shrink: 0;
  }
  .nav-cta:hover { background: var(--gold); }

  /* ── Hamburger — hidden on desktop ── */
  .mob-ham {
    display: none;
    align-items: center;
    justify-content: center;
    background: none;
    border: 1px solid var(--border-soft);
    border-radius: 4px;
    padding: 7px 9px;
    cursor: pointer;
    flex-shrink: 0;
    transition: border-color 0.2s;
  }
  .mob-ham:hover { border-color: rgba(245,200,0,0.3); }

  /* ── Mobile dropdown ── */
  .landing-nav-mobile {
    position: fixed;
    top: 63px; left: 0; right: 0;
    background: rgba(10, 9, 0, 0.97);
    border-bottom: 1px solid var(--border);
    padding: 16px 20px 28px;
    z-index: 199;
    display: flex;
    flex-direction: column;
    gap: 0;
  }
  .mob-link {
    width: 100%;
    text-align: left;
    background: none;
    border: none;
    border-bottom: 1px solid var(--border-soft);
    color: var(--muted-light);
    font-size: 13px;
    letter-spacing: 2px;
    text-transform: uppercase;
    padding: 13px 0;
    cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    font-weight: 300;
    transition: color 0.2s;
  }
  .mob-link:hover { color: var(--white); }
  .mob-link:last-of-type { border-bottom: none; }
  .mob-cta {
    background: var(--accent);
    color: var(--black);
    text-align: center;
    padding: 14px;
    border-radius: 2px;
    font-weight: 500;
    margin-top: 16px;
    border: none;
    cursor: pointer;
    font-size: 12px;
    letter-spacing: 2px;
    text-transform: uppercase;
    font-family: 'DM Sans', sans-serif;
    transition: background 0.25s;
    width: 100%;
  }
  .mob-cta:hover { background: var(--gold); }

  /* ════════════════════════════════
     RESPONSIVE BREAKPOINTS
  ════════════════════════════════ */

  /* Tablet — shrink padding, smaller gaps */
  @media (max-width: 1024px) {
    .landing-nav { padding: 16px 32px; }
    .nav-links { gap: 22px; }
    .nav-links button { font-size: 10px; }
  }

  /* Small tablet — hide desktop links, show hamburger */
  @media (max-width: 860px) {
    .landing-nav { padding: 14px 24px; }
    .nav-links { display: none !important; }
    .nav-cta.desktop-cta { display: none !important; }
    .mob-ham { display: inline-flex !important; }
    .landing-nav-mobile { top: 61px; }
  }

  /* Mobile */
  @media (max-width: 640px) {
    .landing-nav { padding: 12px 16px; }
    .landing-logo-img { height: 30px; }
    .landing-nav-mobile { top: 57px; padding: 12px 16px 24px; }
    .mob-link { font-size: 12px; padding: 12px 0; }
  }

  /* Extra small */
  @media (max-width: 380px) {
    .landing-nav { padding: 10px 12px; }
    .landing-logo-img { height: 26px; }
    .mob-ham { padding: 6px 8px; }
  }
`;

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const NAV_LINKS = [
  { label: 'Services', target: 'services' },
  { label: 'Work', target: 'work' },
  { label: 'About', target: 'about' },
  { label: 'Blog', target: 'blog' },
  { label: 'Process', target: 'process' },
  { label: 'FAQ', target: 'faq' },
  { label: 'Testimonials', target: 'testimonials' },
  { label: 'Contact', target: 'contact' },
];

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const LandingNav = ({ mobileMenu, setMobileMenu }) => {
  const [localOpen, setLocalOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  /* Support both controlled (parent passes mobileMenu/setMobileMenu)
     and uncontrolled (self-managed) usage */
  const isOpen = typeof mobileMenu === 'boolean' ? mobileMenu : localOpen;
  const setOpen = typeof setMobileMenu === 'function' ? setMobileMenu : setLocalOpen;

  /* ── Navigation handler ── */
  const handleNav = (target) => {
    setOpen(false);

    /* Pages that have their own route */
    if (target === 'about') { navigate('/about'); return; }
    if (target === 'blog') { navigate('/blog'); return; }
    if (target === 'work') { navigate('/work'); return; }

    /* Hash-scroll on landing page */
    if (location.pathname === '/') {
      const el = document.getElementById(target);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', `#${target}`);
        return;
      }
    }

    /* Navigate to landing then scroll */
    navigate({ pathname: '/', hash: `#${target}` });
  };

  return (
    <>
      <style>{navStyles}</style>

      {/* ── Navbar ── */}
      <nav className="landing-nav" role="navigation" aria-label="Main navigation">

        {/* Logo */}
        <a href="/" className="landing-logo" aria-label="WebDigiting home">
          <img src={wdLogo} alt="" className="landing-logo-img" width={59} height={36} decoding="async" fetchpriority="high" />
        </a>

        {/* Desktop links */}
        <ul className="nav-links">
          {NAV_LINKS.map((link) => (
            <li key={link.target}>
              <button
                type="button"
                onClick={() => handleNav(link.target)}
                aria-label={`Navigate to ${link.label}`}
              >
                {link.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Desktop CTA */}
        <button
          type="button"
          className="nav-cta desktop-cta"
          onClick={() => handleNav('contact')}
        >
          Let's Talk
        </button>

        {/* Hamburger — visible on mobile only via CSS */}
        <button
          type="button"
          className="mob-ham"
          onClick={() => setOpen(!isOpen)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isOpen}
        >
          {isOpen ? (
            /* X icon when open */
            <svg
              viewBox="0 0 24 24" fill="none" stroke="var(--white)"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ width: 18, height: 18 }}
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            /* Hamburger icon when closed */
            <svg
              viewBox="0 0 24 24" fill="none" stroke="var(--white)"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ width: 18, height: 18 }}
            >
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          )}
        </button>
      </nav>

      {/* ── Mobile dropdown ── */}
      {isOpen && (
        <div className="landing-nav-mobile" role="dialog" aria-label="Mobile navigation">
          {NAV_LINKS.map((link) => (
            <button
              key={link.target}
              type="button"
              className="mob-link"
              onClick={() => handleNav(link.target)}
            >
              {link.label}
            </button>
          ))}
          <button
            type="button"
            className="mob-cta"
            onClick={() => handleNav('contact')}
          >
            Let's Talk
          </button>
        </div>
      )}
    </>
  );
};

export default LandingNav;